/**
 * 🌊 **The water moves — T207.**
 *
 * 🎯 **haugur, 2026-09-15:** *"so iceland is known for its waterfalls, there is
 * a lot of them :) what do we need to do to get the rivers moving and
 * waterfalls working."*
 *
 * ## What this is
 *
 * ✅ **The flame's method, pointed at water** — a strip of frames uploaded once
 * as layers of a texture array, with the shader picking a layer per frame.
 * `Development/Minecraft/15_Animated_Textures_And_The_Flame.md` §1: an animated
 * texture is an ordinary texture with the frames stacked. **Nothing here runs
 * in the shader**, so this is not the kind of change that has twice blacked out
 * both environments.
 *
 * Each frame is the water tile we already ship, **multiplied by a moving field
 * of soft chop**. The photograph supplies the colour; this supplies the life —
 * the division `Design/00` §12.7 asks for: *"The interest must come from
 * animation, not from the tile."*
 *
 * ## 🔴 Three routes were measured and thrown away first, and that is the value here
 *
 * **① Scrolling the tile — what Minecraft does — draws a grid line every metre.**
 * Our water tile is a **photograph and it does not wrap**: the step from its
 * last column to its first measures **2.35** against **0.39** for two
 * neighbouring columns inside it — *six times the ordinary step*. That seam
 * already stands between two water blocks today and is invisible only because
 * nothing moves. Sliding the tile would drag it across the whole surface.
 *
 * **② Warping the pixels WITHIN the tile moves nothing you can see.** Measured
 * before it was believed: mean change between two frames a quarter-loop apart
 * was **0.4 of 255**, and pushing the displacement from 2 texels to 12 took it
 * only to 0.65. 🔴 **The reason is the tile itself — it spans 16.7 luminance
 * levels out of 255.** *A near-featureless surface has nothing in it to
 * rearrange.* ⚠️ **It would have shipped green and looked like nothing at all.**
 *
 * **③ A handful of sine waves makes a QUILT, not water.** The first field that
 * was visible enough was four sinusoids — and baked, it was a **regular
 * diagonal lattice of dots**, with a diamond grid plainly visible once the tile
 * was repeated 4×4. ⚠️ **A small sum of sinusoids can only ever be a lattice**;
 * that is what they are. 🔴 **Found by baking the picture and looking at it,
 * with every test passing and the movement measurement saying it was fine** —
 * the number said *it moves*, and only the picture said *it moves like a woven
 * blanket*.
 *
 * ## ✅ So: band-limited noise that still wraps exactly
 *
 * The field is a sum of **many** sinusoids — every integer frequency pair in an
 * annulus — each given a **seeded random phase** and its own whole-number
 * rotation speed. Many components with scattered phases is noise; four is a
 * lattice. It is the standard way to build seamless looping noise and it keeps
 * both guarantees:
 *
 * | | |
 * |---|---|
 * | ✅ **It wraps across a block boundary** | every spatial frequency is a **whole number of periods across the tile**, so the field's value *and slope* continue into the next block. Measured: the step across the boundary is 0.127 against 0.138 for two columns inside the tile — **no seam at all** |
 * | ✅ **The loop closes exactly** | every temporal speed is a whole number of turns per loop, so frame {@link RIPPLE_FRAMES} is frame 0 to floating-point zero |
 *
 * 🔴 **And the band is chosen, not guessed.** ⚠️ **Every water block in the
 * world shows this same tile at the same moment** — that is what an animated
 * texture *is*, and it cannot be otherwise without per-block data. So anything
 * low-frequency pulses in unison on a one-metre grid. {@link RIPPLE_BAND} keeps
 * the field fine enough that the eye does not track a one-metre period in it.
 *
 * ⚠️ **The photograph's own 2.35 seam is untouched and still there.** It is not
 * this feature's to fix — see **T209**.
 *
 * ## ⚠️ Where `Design/00` §12.7 has gone stale — noted, not quietly corrected
 *
 * §12.7 specifies water as *"broad, soft horizontal chop bands at ±8 %"*, which
 * described the **procedural** tile. Water has had an authored photograph since
 * the texture pack (`vatn-all`), measured at **±15 %**, not ±8. The ±8 % lives
 * on here as the amplitude of the *moving* field. **The word that did not
 * survive is *broad*, and §3 above is why.**
 */

/**
 * How many frames the loop has.
 *
 * ⚠️ **Each one is a full tile in GPU memory**: at 64 px that is 16 KB a frame,
 * so 24 frames cost **384 KB**. Cheap, but not free, and the first number to
 * look at if the texture upload ever has to shrink.
 */
export const RIPPLE_FRAMES = 24;

/**
 * ⏱️ **How long one frame is held, in real milliseconds.**
 *
 * 24 frames at 140 ms is a **3.4-second loop**, which is in the range of real
 * swell. 🔴 **Real milliseconds, not game time** — the sea does not run 72×
 * faster because the world clock does. The same rule the flame follows, and for
 * the same reason.
 */
export const RIPPLE_FRAME_MS = 140;

/**
 * 🌊 **How strong the chop is**, as a fraction of the tile's own brightness.
 *
 * `0.08` is ±8 %, which is `Design/00` §12.7's number for water. On a tile
 * whose mean is 57 that is **±4.6 levels against the tile's own span of 16.7** —
 * plainly visible, and still far below anything that would read as a pattern
 * laid over the sea.
 *
 * ⚠️ **The one number here that is a judgement rather than a measurement.** It
 * is the dial to turn if haugur says the water looks dead, or says it looks
 * like a bedsheet.
 */
export const RIPPLE_CONTRAST = 0.08;

/**
 * 🔴 **The frequency band, in whole periods across one metre of water.**
 *
 * Below 4, a feature is a quarter of a block or bigger and the one-metre repeat
 * becomes legible — the lattice fault above, arriving by a different door.
 * Above 11, features are under 9 cm and the first mip level averages them away,
 * so the movement would vanish at any distance. **The band is what is left.**
 */
export const RIPPLE_BAND = { low: 4, high: 11 } as const;

/**
 * How fast the slowest and fastest components turn, in whole turns per loop.
 *
 * ⚠️ **Whole numbers, or the loop stops closing.** Mixed speeds are what stop
 * the field reading as one travelling wave; the sign is randomised per
 * component, so half of it goes the other way.
 */
const TURNS = { low: 1, high: 3 } as const;

/** Fixed, so every machine and every reload builds the same water. */
const RIPPLE_SEED = 0x5ea_0f00d;

/**
 * 🔴 **The field is built at 32 × 32 whatever the tile is, and then sampled.**
 *
 * ⚠️ **This is a measurement, not a corner cut.** {@link RIPPLE_BAND} tops out
 * at 11 periods across the tile, and 32 samples carry frequencies up to 16 —
 * comfortably above it, so **nothing in the field is lost**. Building at 64
 * instead costs four times the work for information that is not there.
 *
 * 🔴 **It matters because this runs at startup on the player's machine**, and
 * the player we know most about is the one who said the game was laggy.
 * Measured here: **106 ms at 64, 27 ms at 32.**
 */
const FIELD_TILE = 32;

/* eslint-disable security/detect-object-injection --
   Everything below this line is numeric work over typed arrays: the fields are
   Float32Arrays and the tiles Uint8Arrays, every one of them indexed by a loop
   counter bounded above. `security/detect-object-injection` exists to catch
   prototype pollution through plain-object property access, which a typed array
   cannot suffer, and it fires on every element read and write here. Same
   reasoning and same wording as `textures.ts`, which does the same maths a few
   files along. */

/** Which frame is showing at a given moment. Wraps, so it never runs out. */
export function rippleFrameAt(elapsedMs: number): number {
  const step = Math.floor(elapsedMs / RIPPLE_FRAME_MS);
  return ((step % RIPPLE_FRAMES) + RIPPLE_FRAMES) % RIPPLE_FRAMES;
}

/**
 * ⚠️ Seeded, never `Math.random` — the same rule and the same generator as
 * `textures.ts`. Water that differed between two players looking at the same
 * sea would be a bug nobody could reproduce.
 */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Component {
  /** Whole periods across the tile. */
  readonly fx: number;
  readonly fy: number;
  /** Whole turns per loop, signed. */
  readonly turns: number;
  readonly phase: number;
  readonly weight: number;
}

/**
 * Every frequency in the band, once each, with a seeded phase and speed.
 *
 * 🔴 **Half the plane only.** A sine and its negative-frequency twin are the
 * same wave, so taking both would double every component and halve the
 * randomness for no gain — the field would be symmetric and start looking like
 * a lattice again.
 *
 * The weight falls as `1/|f|`, which is what a real water surface does: the big
 * slow shapes carry the energy and the fine chop rides on top.
 */
function components(): readonly Component[] {
  const random = rng(RIPPLE_SEED);
  const out: Component[] = [];
  const { low, high } = RIPPLE_BAND;

  for (let fy = -high; fy <= high; fy++) {
    for (let fx = 0; fx <= high; fx++) {
      /* The half-plane: drop the mirror of everything already taken. */
      if (fx === 0 && fy <= 0) continue;
      const mag = Math.hypot(fx, fy);
      if (mag < low || mag > high) continue;
      const span = TURNS.high - TURNS.low;
      const turns = (TURNS.low + Math.floor(random() * (span + 1))) * (random() < 0.5 ? -1 : 1);
      out.push({ fx, fy, turns, phase: random() * Math.PI * 2, weight: 1 / mag });
    }
  }
  return out;
}

/**
 * The chop for every frame, each `tile × tile` of **−1…1**.
 *
 * ⚠️ **Built once and cached per tile size.** It is ~90 components over 24
 * frames, and the client asks for it while the first chunk is still arriving.
 *
 * 🔴 **The frame loop is NOT `sin()` per texel per frame, and that is why this
 * is fast enough to do at startup.** `sin(a + b)` is expanded so the spatial
 * half — which does not depend on the frame — is computed once per component
 * and then folded into all 24 frames with multiply-adds. It turns ~8.5 million
 * transcendental calls into ~740 thousand.
 */
let FIELDS: readonly Float32Array[] | null = null;

export function rippleFields(): readonly Float32Array[] {
  if (FIELDS) return FIELDS;

  const tile = FIELD_TILE;
  const n = tile * tile;
  const frames: Float32Array[] = [];
  for (let k = 0; k < RIPPLE_FRAMES; k++) frames.push(new Float32Array(n));

  const sinA = new Float32Array(n);
  const cosA = new Float32Array(n);

  for (const c of components()) {
    for (let y = 0; y < tile; y++) {
      for (let x = 0; x < tile; x++) {
        const a = (2 * Math.PI * (c.fx * x + c.fy * y)) / tile + c.phase;
        const i = y * tile + x;
        sinA[i] = Math.sin(a);
        cosA[i] = Math.cos(a);
      }
    }
    for (let k = 0; k < RIPPLE_FRAMES; k++) {
      /* sin(A + B) = sin A cos B + cos A sin B, with B the frame's rotation. */
      const b = (2 * Math.PI * c.turns * k) / RIPPLE_FRAMES;
      const cb = Math.cos(b) * c.weight;
      const sb = Math.sin(b) * c.weight;
      const frame = frames[k];
      if (!frame) continue;
      for (let i = 0; i < n; i++) {
        frame[i] = (frame[i] ?? 0) + (sinA[i] ?? 0) * cb + (cosA[i] ?? 0) * sb;
      }
    }
  }

  /**
   * 🔴 **Normalised by the spread, not by the single largest value.**
   *
   * A sum of ~90 sinusoids is very nearly Gaussian, so its extreme is a rare
   * spike a long way above the typical value. Dividing by that extreme would
   * squash everything anybody actually sees into the middle of the range and
   * the water would go still again — **the invisible-warp fault, arriving by
   * arithmetic.** ⚠️ Two standard deviations is the scale that means something;
   * the tail is clamped, and it is a few texels a frame.
   */
  let sum = 0;
  for (const frame of frames) for (const v of frame) sum += v * v;
  const rms = Math.sqrt(sum / (n * RIPPLE_FRAMES));
  const scale = rms > 0 ? 1 / (2 * rms) : 0;
  for (const frame of frames) {
    for (let i = 0; i < n; i++) {
      frame[i] = Math.max(-1, Math.min(1, (frame[i] ?? 0) * scale));
    }
  }

  FIELDS = frames;
  return frames;
}

/**
 * The chop at one point of one frame, **−1…1**.
 *
 * `x` and `y` are in texels of a tile `tile` across, and both **wrap** — so a
 * test can ask for column `tile` and be told what the next block along shows at
 * column 0, which is the seam guarantee stated as a question.
 *
 * ⚠️ **Bilinear between the field's own 32 samples, and wrapping at the edge
 * rather than clamping.** A clamp here would flatten the last half-texel of
 * every tile into a constant and put back a faint seam of exactly the kind this
 * file exists to avoid.
 */
export function chopAt(x: number, y: number, tile: number, frame: number): number {
  const frames = rippleFields();
  const field = frames[((frame % RIPPLE_FRAMES) + RIPPLE_FRAMES) % RIPPLE_FRAMES];
  if (!field) return 0;

  const n = FIELD_TILE;
  const fx = (x * n) / tile;
  const fy = (y * n) / tile;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const wrap = (v: number): number => ((v % n) + n) % n;
  const xa = wrap(x0);
  const xb = wrap(x0 + 1);
  const ya = wrap(y0);
  const yb = wrap(y0 + 1);

  const v00 = field[ya * n + xa] ?? 0;
  const v10 = field[ya * n + xb] ?? 0;
  const v01 = field[yb * n + xa] ?? 0;
  const v11 = field[yb * n + xb] ?? 0;
  return (v00 * (1 - tx) + v10 * tx) * (1 - ty) + (v01 * (1 - tx) + v11 * tx) * ty;
}

/**
 * 🌊 **One frame of moving water**, from the still tile we already ship.
 *
 * @param base the authored water tile, RGBA, `tile × tile`
 * @param tile texels along one edge
 * @param frame 0…{@link RIPPLE_FRAMES}−1, and it wraps
 * @param contrast ⚠️ **a parameter only so `tools/textures/ripple-sheet.mjs`
 *   can sweep it and put candidates in front of haugur.** The game always uses
 *   {@link RIPPLE_CONTRAST}; nothing in the client passes this.
 *
 * ⚠️ **The alpha channel is copied, never scaled.** Water is opaque in the tile
 * and its transparency is the material's business; multiplying alpha here would
 * make the sea flicker between solid and see-through.
 */
export function rippleFrame(
  base: Uint8Array,
  tile: number,
  frame: number,
  contrast: number = RIPPLE_CONTRAST,
): Uint8Array {
  const out = new Uint8Array(base.length);
  for (let y = 0; y < tile; y++) {
    for (let x = 0; x < tile; x++) {
      const scale = 1 + contrast * chopAt(x, y, tile, frame);
      const i = (y * tile + x) * 4;
      for (let c = 0; c < 3; c++) {
        out[i + c] = Math.max(0, Math.min(255, Math.round((base[i + c] ?? 0) * scale)));
      }
      out[i + 3] = base[i + 3] ?? 255;
    }
  }
  return out;
}
